import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';

function StoryCard({ story }) {
  const { title, story: storyText, tags, audioUrl } = story;
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlayPause = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    const current = audioRef.current.currentTime;
    const duration = audioRef.current.duration;
    setCurrentTime(current);
    setProgress((current / duration) * 100);
  };

  const handleScrub = (event) => {
    const scrubTime = (event.nativeEvent.offsetX / event.target.offsetWidth) * audioRef.current.duration;
    audioRef.current.currentTime = scrubTime;
  };

  const handleLoadedMetadata = () => {
    setDuration(audioRef.current.duration);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleShare = async () => {
    const shareText = `${title}\n\n...erstellt mit Plottie.de✨`;
    
    try {
      if (navigator.share) {
        try {
          // Fetch the audio file from Firebase Storage
          const response = await fetch(audioUrl);
          const blob = await response.blob();
          const audioFile = new File([blob], `${title}.mp3`, { type: 'audio/mpeg' });
          
          // Share with audio file
          await navigator.share({
            title: title,
            text: shareText,
            files: [audioFile]
          });
        } catch (audioError) {
          console.error('Error preparing audio for share:', audioError);
          // Fallback without file if fetching/sharing file fails
          await navigator.share({
            title: title,
            text: shareText,
            url: 'https://plottie.de'
          });
        }
      }
    } catch (error) {
      console.error('Share error:', error);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(audioUrl, {
        headers: {
          'Origin': window.location.origin,
        },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Get download count from localStorage
      const downloadKey = `download_count_${title}`;
      const currentCount = parseInt(localStorage.getItem(downloadKey) || '0');
      const suffix = currentCount > 0 ? ` (${currentCount})` : '';
      
      // Increment count for next download
      localStorage.setItem(downloadKey, (currentCount + 1).toString());
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}${suffix}.mp3`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading:', error);
    }
  };

  return (
    <motion.div 
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      transition={{ 
        duration: 0.2,
        ease: [0.4, 0, 0.2, 1]
      }}
      className="bg-[#FFF3DD] rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.12)] text-[#343434] mb-6 max-h-[600px] flex flex-col h-full md:h-auto z-[60] relative"
    >
      {/* Title and Tags Section */}
      <div className="mb-2">
        <h2 className="font-freeman text-2xl mb-4 text-[#343434] leading-[110%] tracking-[-0.03rem]">{title}</h2>        
        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, index) => (
            <span 
              key={index}
              className="bg-[#ffffff] text-[#343434] px-2.5 py-1.5 rounded-full font-['Aleo'] text-[0.75rem] font-medium leading-none tracking-[-0.00875rem] flex items-center"
            >
              <span className="translate-y-[0.5px]">{tag}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Custom Audio Player */}
      {audioUrl && (
        <div className="mb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center flex-1 gap-4">
              <span className="text-xs text-[#343434] whitespace-nowrap">
                {formatTime(currentTime)}
              </span>
              
              <div 
                className="relative h-1 bg-[#E8B489] rounded-full cursor-pointer overflow-hidden flex-1"
                onClick={handleScrub}
              >
                <div 
                  className="absolute left-0 top-0 h-full bg-[#7785FC] rounded-full transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
              
              <span className="text-xs text-[#343434] whitespace-nowrap">
                {formatTime(duration)}
              </span>
            </div>

            <button 
              onClick={togglePlayPause} 
              className="w-12 h-12 bg-[#7785FC] text-white rounded-full flex items-center justify-center hover:bg-[#6674eb] transition-colors flex-shrink-0"
            >
              {isPlaying ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16"/>
                  <rect x="14" y="4" width="4" height="16"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>
            
            <audio
              ref={audioRef}
              src={audioUrl}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={() => setIsPlaying(false)}
              className="hidden"
            />
          </div>
        </div>
      )}

      {/* Story Text */}
      <div className="bg-white rounded-lg p-4 flex-1 overflow-y-auto">
        <p className="font-['Aleo'] text-[1rem] font-normal leading-[160%] text-[#343434] lg:w-[90%] whitespace-pre-wrap">{storyText}</p>
      </div>

      <div className="mt-4 flex gap-4">
        <button
          onClick={handleDownload}
          className="flex-1 h-12 bg-[#FFA23E] text-[343434] rounded-full flex items-center justify-center hover:bg-[#FF9925] transition-colors font-freeman text-[1.125rem] tracking-[-0.02em]"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
          Speichern
        </button>
        
        {/* Only show share button on mobile */}
        {typeof navigator.share !== 'undefined' && (
          <button
            onClick={handleShare}
            className="flex-1 h-12 bg-[#EDD1BA] text-[343434] rounded-full flex items-center justify-center hover:bg-[#E8B489] transition-colors font-freeman text-[1.125rem] tracking-[-0.02em]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
            </svg>
            Teilen
          </button>
        )}
      </div>
    </motion.div>
  );
}

export default StoryCard;
